<script>
import notificationService from '@/shared/services/notification.service';
import { savedFilterService } from '@/shared/services/saved-filter.service';

/**
 * Enregistrer le réglage courant d'une liste, et le rappeler.
 *
 * Un réglage se perd en quittant l'écran, et se refait de mémoire à la visite
 * suivante. Deux commandes suffisent: « Enregistrer » met de côté les critères
 * actifs sous un nom, la liste déroulante les repose et relance la recherche.
 *
 * Ce sont les critères qui sont gardés, jamais les résultats: un préréglage
 * rappelé la semaine prochaine montre les données de la semaine prochaine.
 */
export default {
  name: 'SavedFilters',
  props: {
    /** L'écran auquel ces préréglages appartiennent. */
    scope: { type: String, required: true },
    /** Les critères actifs, tels qu'on les enregistrerait. */
    criteria: { type: Object, default: () => ({}) },
    /** Le préréglage appliqué, pour le montrer comme tel. */
    appliedId: { type: String, default: null },
  },
  emits: ['apply', 'loaded'],
  data() {
    return {
      filters: [],
      loading: false,
      search: '',
      listOpen: false,
      formOpen: false,
      name: '',
      makeDefault: false,
      saving: false,
    };
  },
  computed: {
    hasCriteria() {
      return Object.keys(this.criteria || {}).length > 0;
    },

    /** La recherche porte sur les noms: c'est tout ce qui distingue deux vues. */
    visibleFilters() {
      const needle = this.search.trim().toLowerCase();

      if (!needle) {
        return this.filters;
      }

      return this.filters.filter((filter) =>
        filter.name.toLowerCase().includes(needle),
      );
    },

    appliedName() {
      const match = this.filters.find((filter) => filter.id === this.appliedId);
      return match ? match.name : null;
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onEscape);
    this.fetchFilters();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onEscape);
  },
  methods: {
    async fetchFilters() {
      this.loading = true;

      try {
        this.filters = await savedFilterService.list(this.scope);

        // L'ecran s'ouvre sur le prereglage marque par defaut, s'il y en a un.
        const byDefault = this.filters.find((filter) => filter.isDefault);

        this.$emit('loaded', byDefault ?? null);
      } catch (error) {
        // Un ecran de liste reste utilisable sans ses prereglages: on n'affiche
        // rien plutot que de bloquer la liste.
        this.filters = [];
        this.$emit('loaded', null);
      } finally {
        this.loading = false;
      }
    },

    toggleList() {
      this.listOpen = !this.listOpen;
      this.formOpen = false;
      this.search = '';

      // La liste s'ouvre sur son champ de recherche: sans le curseur dedans,
      // on tape dans le vide en croyant filtrer les prereglages.
      if (this.listOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    },

    openForm() {
      if (!this.hasCriteria) {
        return;
      }

      this.formOpen = !this.formOpen;
      this.listOpen = false;
      this.name = this.appliedName || '';
      this.makeDefault = false;

      this.$nextTick(() => this.$refs.nameInput?.focus());
    },

    apply(filter) {
      this.listOpen = false;
      this.$emit('apply', filter);
    },

    async save() {
      const name = this.name.trim();

      if (!name || this.saving) {
        return;
      }

      const existing = this.filters.find(
        (filter) => filter.name.toLowerCase() === name.toLowerCase(),
      );

      // Deux prereglages du meme nom ne se distingueraient pas dans la liste:
      // l'API refuse le doublon, et on propose donc de remplacer celui qui est
      // deja la plutot que d'echouer sur un conflit.
      if (existing) {
        const answer = await notificationService.confirm(
          'Remplacer ce filtre ?',
          `« ${name} » existe déjà. Ses critères seront remplacés par ceux-ci.`,
          { confirmText: 'Remplacer' },
        );

        if (!answer.isConfirmed) {
          return;
        }
      }

      this.saving = true;

      try {
        const saved = existing
          ? await savedFilterService.update(existing.id, {
              criteria: this.criteria,
              isDefault: this.makeDefault,
            })
          : await savedFilterService.create({
              scope: this.scope,
              name,
              criteria: this.criteria,
              isDefault: this.makeDefault,
            });

        await this.fetchFilters();

        this.formOpen = false;
        this.name = '';
        this.$emit('apply', saved);
        notificationService.toastSuccess(
          `Filtre « ${saved.name} » enregistré.`,
        );
      } catch (error) {
        notificationService.toastError(
          error.response?.data?.message ||
            "Le filtre n'a pas pu être enregistré.",
        );
      } finally {
        this.saving = false;
      }
    },

    async setDefault(filter) {
      try {
        await savedFilterService.update(filter.id, {
          isDefault: !filter.isDefault,
        });
        await this.fetchFilters();
      } catch (error) {
        notificationService.toastError(
          "Le filtre par défaut n'a pas pu être changé.",
        );
      }
    },

    async remove(filter) {
      const answer = await notificationService.confirm(
        'Supprimer ce filtre ?',
        `« ${filter.name} » ne sera plus proposé.`,
        { confirmText: 'Supprimer' },
      );

      if (!answer.isConfirmed) {
        return;
      }

      try {
        await savedFilterService.remove(filter.id);
        await this.fetchFilters();
        notificationService.toastSuccess('Filtre supprimé.');
      } catch (error) {
        notificationService.toastError("Le filtre n'a pas pu être supprimé.");
      }
    },

    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.listOpen = false;
        this.formOpen = false;
      }
    },

    onEscape(event) {
      if (event.key === 'Escape') {
        this.listOpen = false;
        this.formOpen = false;
      }
    },
  },
};
</script>

<template>
  <div class="saved-filters">
    <button
      type="button"
      class="list-btn list-btn--ghost"
      :disabled="!hasCriteria"
      :title="
        hasCriteria
          ? 'Enregistrer les critères actifs'
          : 'Réglez au moins un critère pour pouvoir l’enregistrer'
      "
      data-testid="saved-filter-save"
      @click="openForm"
    >
      <i class="ri-bookmark-line"></i>
      Enregistrer le filtre
    </button>

    <div class="saved-filters__anchor">
      <button
        type="button"
        class="list-btn list-btn--ghost saved-filters__picker"
        :class="{ 'saved-filters__picker--applied': Boolean(appliedName) }"
        :aria-expanded="listOpen ? 'true' : 'false'"
        data-testid="saved-filter-picker"
        @click="toggleList"
      >
        <i class="ri-folder-3-line"></i>
        <span class="saved-filters__picker-label">
          {{ appliedName || 'Filtres enregistrés' }}
        </span>
        <span v-if="filters.length" class="saved-filters__badge">
          {{ filters.length }}
        </span>
        <i class="ri-arrow-down-s-line"></i>
      </button>

      <div
        v-if="listOpen"
        class="saved-filters__menu"
        data-testid="saved-filter-menu"
      >
        <div class="saved-filters__search">
          <i class="ri-search-line"></i>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            placeholder="Rechercher un filtre…"
            data-testid="saved-filter-search"
          />
        </div>

        <p v-if="loading" class="saved-filters__state">Chargement…</p>

        <p v-else-if="!filters.length" class="saved-filters__state">
          Aucun filtre enregistré pour cet écran.
        </p>

        <p v-else-if="!visibleFilters.length" class="saved-filters__state">
          Aucun filtre ne porte ce nom.
        </p>

        <ul v-else class="saved-filters__list">
          <li
            v-for="filter in visibleFilters"
            :key="filter.id"
            class="saved-filters__item"
            :class="{
              'saved-filters__item--applied': filter.id === appliedId,
            }"
          >
            <button
              type="button"
              class="saved-filters__apply"
              :title="`Appliquer « ${filter.name} »`"
              @click="apply(filter)"
            >
              <i class="ri-filter-3-line"></i>
              <span>{{ filter.name }}</span>
              <span v-if="filter.isDefault" class="saved-filters__default-tag">
                par défaut
              </span>
            </button>

            <button
              type="button"
              class="saved-filters__icon"
              :title="
                filter.isDefault
                  ? 'Ne plus appliquer à l’ouverture'
                  : 'Appliquer à l’ouverture de l’écran'
              "
              @click.stop="setDefault(filter)"
            >
              <i
                :class="filter.isDefault ? 'ri-star-fill' : 'ri-star-line'"
              ></i>
            </button>

            <button
              type="button"
              class="saved-filters__icon saved-filters__icon--danger"
              title="Supprimer ce filtre"
              @click.stop="remove(filter)"
            >
              <i class="ri-delete-bin-line"></i>
            </button>
          </li>
        </ul>
      </div>

      <div
        v-if="formOpen"
        class="saved-filters__menu saved-filters__menu--form"
        data-testid="saved-filter-form"
      >
        <label class="saved-filters__field">
          <span>Nom du filtre</span>
          <input
            ref="nameInput"
            v-model="name"
            type="text"
            maxlength="80"
            placeholder="Ex. : professionnels sans enquête"
            data-testid="saved-filter-name"
            @keyup.enter="save"
          />
        </label>

        <label class="saved-filters__checkbox">
          <input v-model="makeDefault" type="checkbox" />
          <span>Appliquer à l'ouverture de cet écran</span>
        </label>

        <div class="saved-filters__actions">
          <button
            type="button"
            class="list-btn list-btn--ghost"
            @click="formOpen = false"
          >
            Annuler
          </button>
          <button
            type="button"
            class="list-btn list-btn--primary"
            :disabled="!name.trim() || saving"
            data-testid="saved-filter-submit"
            @click="save"
          >
            {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
